/**
 * @fileoverview Subdomain Configuration - Centralized subdomain routing configuration
 * @module @puffin-core/subdomain-routing/config/subdomainConfig
 * @description Provides centralized configuration for subdomain-based multi-tenant routing.
 * Brand-agnostic — each consuming app MUST set VITE_BASE_DOMAIN env var.
 *
 * Architecture:
 * - Development access modes (localhost, IP addresses, dev subdomains)
 * - Reserved subdomain detection (deferred to backend for authoritative list)
 * - Environment-driven configuration with sensible defaults
 */

import { isBareLocalHost, isOnBaseDomain } from '@puffin-core/cross-app-policy';
import { RESERVED_SUBDOMAINS } from '@puffin-core/reserved-subdomains';
import { getEnvironmentVariable } from './environment.js';

/**
 * Base domain for multi-tenant subdomains.
 * This is THE SINGLE SOURCE OF TRUTH for domain configuration across the application.
 *
 * Configured via VITE_BASE_DOMAIN environment variable.
 * Each consuming app MUST set this (e.g., 'paydaypuffin.com', 'patrolpuffin.com').
 *
 * @constant
 * @type {string}
 */
export const BASE_DOMAIN = getEnvironmentVariable('VITE_BASE_DOMAIN', '');

/**
 * The base domain is not configured: `VITE_BASE_DOMAIN` is unset or empty.
 *
 * DO-SPEC-DRIFT-001 (2026-10-23): every reader used to answer from an EMPTY domain instead (`acme.` URLs, `null`
 * parses, and `isCompanyDomain` judging every host a company domain). A deployed build reaches this only when its spec
 * row is missing, which the web Dockerfiles' `ARG VITE_BASE_DOMAIN=<domain>` defaults used to hide.
 */
export class BaseDomainUnsetError extends Error {
  constructor() {
    super(
      '@puffin-core/subdomain-routing: VITE_BASE_DOMAIN is not set — the app cannot compose or parse a tenant host ' +
        "(declare it in the web service's DO spec, the app's .env, or its jest setup)",
    );
    this.name = 'BaseDomainUnsetError';
    this.code = 'BASE_DOMAIN_UNSET';
  }
}

/**
 * The configured base domain, or a refusal by name. Every reader that composes or parses a host with the base domain
 * asks through here; a localhost path, which names its tenant without one, never does.
 *
 * @returns {string} the base domain (e.g. 'paydaypuffin.com')
 * @throws {BaseDomainUnsetError} when `VITE_BASE_DOMAIN` is unset or empty
 */
export function requireBaseDomain() {
  if (!BASE_DOMAIN) throw new BaseDomainUnsetError();
  return BASE_DOMAIN;
}

/**
 * Get the full URL for a company subdomain
 *
 * @param {string} subdomain - The company subdomain
 * @returns {string} Full URL (e.g., "acme.paydaypuffin.com")
 * @throws {BaseDomainUnsetError} when `VITE_BASE_DOMAIN` is unset
 */
export function getSubdomainUrl(subdomain) {
  const baseDomain = requireBaseDomain();
  if (!subdomain || typeof subdomain !== 'string') {
    return `yourcompany.${baseDomain}`;
  }
  return `${subdomain.toLowerCase()}.${baseDomain}`;
}

/**
 * Development subdomains that should be treated as development access mode.
 * Configured via VITE_DEV_SUBDOMAINS environment variable (comma-separated).
 * Default: 'dev,demo,sandbox'
 *
 * @constant
 * @type {string[]}
 */
const DEV_SUBDOMAINS = getEnvironmentVariable('VITE_DEV_SUBDOMAINS', 'dev,demo,sandbox')
  .split(',')
  .map((s) => s.trim().toLowerCase())
  .filter((s) => s.length > 0);

/**
 * IPv4 address pattern for detecting raw IP access.
 * @constant
 * @type {RegExp}
 */
const IPV4_PATTERN = /^(\d{1,3}\.){3}\d{1,3}$/;

/**
 * Reserved subdomains that are handled specially by the router.
 * These are NOT available for company registration.
 *
 * This now IS the canonical ecosystem reserved-subdomain SSOT
 * (`@puffin-core/reserved-subdomains`), so the router's reserved-name
 * decisions match the backend's authoritative list exactly (previously this
 * was a narrower hand-maintained subset that could drift). `isRouterReservedSubdomain`
 * and `getRouterReservedSubdomains` consume this alias unchanged.
 *
 * @constant
 * @type {readonly string[]}
 */
const ROUTER_RESERVED_SUBDOMAINS = RESERVED_SUBDOMAINS;

/**
 * Check if a hostname represents an IPv4 address.
 *
 * @param {string} hostname - The hostname to check
 * @returns {boolean} True if hostname is an IPv4 address
 */
export function isIPv4Address(hostname) {
  if (!hostname || typeof hostname !== 'string') {
    return false;
  }
  return IPV4_PATTERN.test(hostname);
}

/**
 * Check if a subdomain is configured as a development access subdomain.
 *
 * @param {string} subdomain - The subdomain to check
 * @returns {boolean} True if subdomain is a dev access subdomain
 */
export function isDevSubdomain(subdomain) {
  if (!subdomain || typeof subdomain !== 'string') {
    return false;
  }
  return DEV_SUBDOMAINS.includes(subdomain.toLowerCase());
}

/**
 * Check if access represents a development/external access mode.
 * This includes localhost, IP addresses, and configured dev subdomains.
 *
 * @param {string} subdomainOrHostname - The subdomain or full hostname to check
 * @returns {boolean} True if this represents development access mode
 */
export function isDevAccessMode(subdomainOrHostname) {
  if (!subdomainOrHostname || typeof subdomainOrHostname !== 'string') {
    return false;
  }

  const normalized = subdomainOrHostname.toLowerCase();

  if (normalized === 'localhost') {
    return true;
  }

  if (DEV_SUBDOMAINS.includes(normalized)) {
    return true;
  }

  return false;
}

/**
 * Check if a subdomain is reserved for system use (frontend routing only).
 *
 * @param {string} subdomain - The subdomain to check
 * @returns {boolean} True if subdomain is reserved for router use
 */
export function isRouterReservedSubdomain(subdomain) {
  if (!subdomain || typeof subdomain !== 'string') {
    return false;
  }
  return ROUTER_RESERVED_SUBDOMAINS.includes(subdomain.toLowerCase());
}

/**
 * Get the list of configured development subdomains.
 *
 * @returns {string[]} Array of dev subdomain strings
 */
export function getDevSubdomains() {
  return [...DEV_SUBDOMAINS];
}

/**
 * Get the list of router-reserved subdomains.
 *
 * @returns {string[]} Array of reserved subdomain strings
 */
export function getRouterReservedSubdomains() {
  return [...ROUTER_RESERVED_SUBDOMAINS];
}

/**
 * Extract the dev environment subdomain from the current hostname.
 * Detects if we're on a dev subdomain like dev.{domain}
 * or a company subdomain under dev like dtownretail.dev.{domain}
 *
 * @returns {string|null} The dev subdomain or null if not in dev environment
 */
export function getDevEnvironmentSubdomain() {
  if (typeof window === 'undefined' || !window.location) {
    return null;
  }

  const hostname = window.location.hostname;

  // The loopback machine itself answers no dev subdomain; any other host (a `<tenant>.localhost`, a LAN IP) is judged
  // against the base domain, so an unset one is refused by name, never a quiet null.
  if (!hostname || isBareLocalHost(hostname)) {
    return null;
  }

  const baseDomain = requireBaseDomain();
  if (!isOnBaseDomain(hostname, baseDomain)) {
    return null;
  }

  const parts = hostname.split('.');
  const baseDomainParts = baseDomain.split('.').length;
  const subdomainParts = parts.slice(0, -baseDomainParts);

  for (const part of subdomainParts) {
    if (DEV_SUBDOMAINS.includes(part.toLowerCase())) {
      return part.toLowerCase();
    }
  }

  return null;
}

/**
 * Check if current environment is a dev environment based on hostname.
 *
 * @returns {boolean} True if on a dev environment subdomain
 */
export function isDevEnvironment() {
  return getDevEnvironmentSubdomain() !== null;
}
